import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { LoadingButton } from '../components/ui/LoadingButton'
import { usePageExit } from '../hooks/usePageExit'
import { formatAuthError } from '../lib/auth-errors'

export function LoginPage() {
  const { phase, login, errorMessage } = useAuth()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loginSucceeded, setLoginSucceeded] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const from =
    (location.state as { from?: string } | null)?.from ?? '/'

  const { isExiting, isComplete: exitComplete } = usePageExit(
    loginSucceeded && phase === 'ready',
  )

  if (exitComplete) {
    return <Navigate to={from} replace />
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setLocalError(null)
    try {
      await login(email.trim(), password)
      setLoginSucceeded(true)
    } catch (error) {
      setSubmitting(false)
      setLocalError(formatAuthError(error))
    }
  }

  return (
    <div
      className={[
        'page',
        'page-narrow',
        'page-transition',
        isExiting ? 'page-transition--exit' : 'page-transition--enter',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="page-header">
        <p className="eyebrow">Maruya RAG</p>
        <h1>ログイン</h1>
      </header>

      <form
        className={['card', 'form-card', submitting ? 'form-card--busy' : '']
          .filter(Boolean)
          .join(' ')}
        onSubmit={(event) => void handleSubmit(event)}
      >
        <label className="field">
          <span>メールアドレス</span>
          <input
            type="email"
            autoComplete="email"
            required
            disabled={submitting}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="field">
          <span>パスワード</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            disabled={submitting}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {(localError || (phase !== 'loading' && errorMessage)) && (
          <p className="form-error">{localError ?? errorMessage}</p>
        )}

        <LoadingButton
          type="submit"
          isLoading={submitting}
          loadingLabel="ログイン中…"
        >
          ログイン
        </LoadingButton>
      </form>

      <p className="muted center-text">
        アカウントをお持ちでない場合は{' '}
        <Link to="/signup">サインアップ</Link>
      </p>
    </div>
  )
}
